import { css } from '@emotion/react';

const resetElements = `
  a,
  abbr,
  acronym,
  address,
  applet,
  article,
  aside,
  audio,
  b,
  big,
  blockquote,
  body,
  button,
  canvas,
  caption,
  center,
  cite,
  code,
  dd,
  del,
  details,
  dfn,
  div,
  dl,
  dt,
  em,
  embed,
  fieldset,
  figcaption,
  figure,
  footer,
  form,
  h1,
  h2,
  h3,
  h4,
  h5,
  h6,
  header,
  hgroup,
  html,
  i,
  iframe,
  img,
  ins,
  kbd,
  label,
  legend,
  li,
  mark,
  menu,
  nav,
  object,
  ol,
  output,
  p,
  pre,
  q,
  ruby,
  s,
  samp,
  section,
  small,
  span,
  strike,
  strong,
  sub,
  summary,
  sup,
  table,
  tbody,
  td,
  tfoot,
  th,
  thead,
  time,
  tr,
  tt,
  u,
  ul,
  var,
  video`;

// Global reset — applied for standalone apps.
// All bare element selectors apply to the whole document.
export const standaloneAppResetStyles = css`
  /* http://meyerweb.com/eric/tools/css/reset/
   v2.0 | 20110126
   License: none (public domain)
  */

  ${resetElements} {
    border: 0;
    font-size: 100%;
    font: inherit;
    margin: 0;
    padding: 0;
    vertical-align: baseline;
    -webkit-tap-highlight-color: transparent;
  }
  /* HTML5 display-role reset for older browsers */
  article,
  aside,
  details,
  figcaption,
  figure,
  footer,
  header,
  hgroup,
  menu,
  nav,
  section {
    display: block;
  }
  a {
    text-decoration: none;
  }
  button {
    background: none;
    cursor: pointer;
  }
  body {
    line-height: 1;
    margin: 0;
  }
  p {
    margin: 0;
  }
  ol,
  ul {
    list-style: none;
  }
  blockquote,
  q {
    quotes: none;
  }
  blockquote:before,
  blockquote:after,
  q:before,
  q:after {
    content: '';
    content: none;
  }
  table {
    border-collapse: collapse;
    border-spacing: 0;
  }
`;

// Scoped reset — applied for embedded widget contexts (e.g. r1-classic-bridge).
// Every selector is prefixed with :where(wrapperClass) so resets never leak to
// the host page. :where() has zero specificity, ensuring the reset stays lower
// than any single Emotion class selector (0,1,0) and never overwrites component
// styles. The standalone reset's `body` rule maps onto the wrapper here: its
// `line-height` is the baseline components inherit, so dropping it would leave
// them inheriting the host page's instead. Its `margin` is not carried over —
// that one really is meaningless inside a wrapper div.
export const scopedAppResetStyles = (wrapperClass: string) => {
  const w = `:where(${wrapperClass})`;
  return css`
    ${w} {
      line-height: 1;
    }
    ${resetElements
      .split(',')
      .map((el) => {
        return `${w} ${el.trim()}`;
      })
      .join(',\n    ')} {
      border: 0;
      font-size: 100%;
      font: inherit;
      margin: 0;
      padding: 0;
      vertical-align: baseline;
      -webkit-tap-highlight-color: transparent;
    }
    ${w} article,
    ${w} aside,
    ${w} details,
    ${w} figcaption,
    ${w} figure,
    ${w} footer,
    ${w} header,
    ${w} hgroup,
    ${w} menu,
    ${w} nav,
    ${w} section {
      display: block;
    }
    ${w} a {
      text-decoration: none;
    }
    ${w} button {
      background: none;
      cursor: pointer;
    }
    ${w} p {
      margin: 0;
    }
    ${w} ol,
    ${w} ul {
      list-style: none;
    }
    ${w} blockquote,
    ${w} q {
      quotes: none;
    }
    ${w} blockquote:before,
    ${w} blockquote:after,
    ${w} q:before,
    ${w} q:after {
      content: '';
      content: none;
    }
    ${w} table {
      border-collapse: collapse;
      border-spacing: 0;
    }

    // Overrides for Kendo styles leaking into the r1-classic-bridge widget.
    ${w} label {
      width: initial;
    }
    ${w} input,
    ${w} select,
    ${w} textarea {
      -webkit-appearance: none;
      appearance: none;
      box-shadow: none;
      min-height: initial !important;
    }
    ${w} button[disabled],
    ${w} input[disabled],
    ${w} select[disabled],
    ${w} textarea[disabled] {
      background: none;
      color: inherit;
      pointer-events: initial;
    }
    ${w} input::placeholder,
    ${w} select::placeholder {
      font-style: normal;
    }
  `;
};
